import {skipToken} from '@reduxjs/toolkit/query';

import {isEmpty, sortBy} from 'ramda';

import {
  useAccItemCodeServiceGetValidCodesQuery,
  useGetListPreAccountingProcessingSettingsQuery,
} from '@dms/api/finance';

import {Nullish} from 'shared/web';

import {useGetCurrentBranch} from './useGetCurrentBranch';
import {usePermissions} from './usePermissions/usePermissions';

export const useAccProductItemCodesOptions = (
  defaultItemCode: string | Nullish,
  currentlyValid = false
) => {
  const {
    data: productItemCodes,
    isLoading: isLoadingProductItemCodes,
    isError: isProductItemCodesError,
  } = useAccItemCodeServiceGetValidCodesQuery(undefined);

  const [hasReadPreAccountingProcessingPermission] = usePermissions({
    permissionKeys: ['readPreAccountingProcessing'],
  });

  const {
    data: preAccountingProcessingSettings,
    isLoading: isLoadingPreAccountingProcessingSettings,
    isError: isErrorPreAccountingProcessingSettings,
  } = useGetListPreAccountingProcessingSettingsQuery(
    hasReadPreAccountingProcessingPermission ? undefined : skipToken
  );

  const {activeBranchId} = useGetCurrentBranch();

  const systemSetting = preAccountingProcessingSettings?.find((settings) => settings.isSystem);

  const productItemCodesOptions = sortBy((item) => item?.itemCode || '', productItemCodes || [])
    .filter((item) => (currentlyValid ? item?.currentlyValid : true))
    .map((item) => ({
      label: `${item?.itemCode} - ${item?.itemCodeName}`,
      value: item?.itemCode ?? '',
    }));

  const defaultItemCodeOptions = [
    {
      label: defaultItemCode,
      value: defaultItemCode,
    },
  ];

  const props = {
    isLoadingProductItemCodes:
      isLoadingProductItemCodes || isLoadingPreAccountingProcessingSettings,
    isProductItemCodesError: isErrorPreAccountingProcessingSettings || isProductItemCodesError,
    shouldShowItemCodesChoice: (
      preAccountingProcessingSettings?.find((item) => item.branchId === activeBranchId) ||
      systemSetting
    )?.notifyAccountingDocuments,
  };

  if (isEmpty(productItemCodes) || !productItemCodes) {
    return {
      productItemCodesOptions: defaultItemCodeOptions,
      ...props,
    };
  }

  return {
    productItemCodesOptions:
      productItemCodesOptions.some((option) => option.value === defaultItemCode) || !defaultItemCode
        ? productItemCodesOptions
        : [...defaultItemCodeOptions, ...productItemCodesOptions],
    ...props,
  };
};
